import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clearSocketBuffer, connectSocket, socket } from '../socket';
import {
  buildGomokuRoomRoute
} from '../games/routes';
import { useAuth } from '../auth/AuthContext';

function roomCodeFromPayload(payload) {
  return String(payload?.roomCode || payload?.room?.code || payload?.code || '').trim().toUpperCase();
}

export function normalizeGomokuRoomCodeInput(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 4);
}

const GomokuLobbyPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const socketActionCleanupRef = useRef(null);

  useEffect(() => () => {
    if (socketActionCleanupRef.current) {
      socketActionCleanupRef.current();
    }
  }, []);

  const runRoomAction = ({
    action,
    successEvent,
    emitEvent,
    emitPayload,
    failureMessage,
    onSuccess
  }) => {
    if (socketActionCleanupRef.current) {
      socketActionCleanupRef.current();
    }

    setError('');
    setBusyAction(action);

    let settled = false;

    const cleanup = () => {
      socket.off('connect', handleConnect);
      socket.off(successEvent, handleSuccess);
      socket.off('error', handleSocketError);
      socket.off('connect_error', handleConnectError);
      if (socketActionCleanupRef.current === cleanup) {
        socketActionCleanupRef.current = null;
      }
    };

    const finish = (callback) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      setBusyAction('');
      callback();
    };

    function handleSuccess(payload) {
      finish(() => onSuccess(payload));
    }

    function handleConnect() {
      socket.emit(emitEvent, emitPayload);
    }

    function handleSocketError(payload = {}) {
      clearSocketBuffer();
      finish(() => setError(payload.message || failureMessage));
    }

    function handleConnectError(errorPayload = {}) {
      clearSocketBuffer();
      finish(() => setError(errorPayload.message || failureMessage));
    }

    socketActionCleanupRef.current = cleanup;
    socket.once(successEvent, handleSuccess);
    socket.once('error', handleSocketError);
    socket.once('connect_error', handleConnectError);
    if (socket.connected) {
      handleConnect();
    } else {
      socket.once('connect', handleConnect);
      connectSocket();
    }
  };

  const handleCreateRoom = () => {
    runRoomAction({
      action: 'create',
      successEvent: 'roomCreated',
      emitEvent: 'createRoom',
      emitPayload: {
        gameType: 'gomoku',
        seatIndex: 0
      },
      failureMessage: '无法创建五子棋房间',
      onSuccess: (payload) => {
        const roomPayload = payload?.room || payload;
        navigate(buildGomokuRoomRoute(roomCodeFromPayload(payload)), {
          state: { room: roomPayload }
        });
      }
    });
  };

  const handleJoinRoom = (event) => {
    event.preventDefault();
    const normalizedCode = normalizeGomokuRoomCodeInput(roomCode);
    if (!normalizedCode) {
      setError('请输入房间码后再加入。');
      return;
    }
    navigate(buildGomokuRoomRoute(normalizedCode));
  };

  return (
    <main className="page-shell page-shell--themed gomoku-lobby-page">
      <header className="page-hero gomoku-hero">
        <div>
          <h1>五子棋大厅</h1>
        </div>
      </header>

      {error ? <div className="error-message">{error}</div> : null}

      <section className="data-card gomoku-lobby-card">

        <button className="gomoku-primary-button" type="button" onClick={handleCreateRoom} disabled={Boolean(busyAction)}>
          {busyAction === 'create' ? '创建中...' : '创建房间'}
        </button>

        <form className="gomoku-join-form" onSubmit={handleJoinRoom}>
          <label className="field-label" htmlFor="gomoku-room-code">
            房间码
          </label>
          <div className="gomoku-join-row">
            <input
              id="gomoku-room-code"
              aria-label="房间码"
              inputMode="numeric"
              maxLength="4"
              pattern="[0-9]{4}"
              placeholder="输入 4 位房间码"
              value={roomCode}
              onChange={(event) => setRoomCode(normalizeGomokuRoomCodeInput(event.target.value))}
            />
            <button className="gomoku-secondary-button" type="submit" disabled={Boolean(busyAction)}>
              加入房间
            </button>
          </div>
        </form>
      </section>

      <nav className="home-links" aria-label="游戏导航">
        <Link className="pill-link" to="/">返回游戏库</Link>
        {user?.role === 'admin' && <Link className="pill-link" to="/admin/gomoku">管理</Link>}
      </nav>
    </main>
  );
};

export default GomokuLobbyPage;
